<template>
  <div class="ugo-form-view-file-list">
    <span v-if="!normalizedFiles.length" class="ugo-form-view-empty">—</span>
    <div v-else class="ugo-form-view-file-cards">
      <div
        v-for="(f, i) in normalizedFiles"
        :key="i"
        class="ugo-form-view-file-card"
      >
        <div class="ugo-form-view-file-card-icon" @click="openPreview(f)">
          <img
            v-if="f.isImage"
            :src="f.url"
            class="ugo-form-view-file-thumb"
            alt=""
          />
          <img
            v-else
            :src="f.iconUrl"
            class="ugo-form-view-file-type-icon"
            alt=""
          />
        </div>
        <div class="ugo-form-view-file-card-body">
          <a
            class="ugo-form-view-file-card-name"
            :title="f.name"
            @click.prevent="openPreview(f)"
          >{{ f.name }}</a>
          <div class="ugo-form-view-file-card-actions">
            <a class="ugo-form-view-file-action" @click.prevent="openPreview(f)">预览</a>
            <span class="ugo-form-view-file-sep">|</span>
            <a class="ugo-form-view-file-action" @click.prevent="download(f.url, f.name)">下载</a>
          </div>
        </div>
      </div>
    </div>
    <FormViewPreviewDrawer
      :visible.sync="previewVisible"
      :url="previewUrl"
      :name="previewFileName"
    />
  </div>
</template>

<script>
import FormViewPreviewDrawer from './FormViewPreviewDrawer.vue';
import {
  toFullFileUrl,
  getCosBase,
  getFileExt,
  isImageExt,
  getFileTypeIconUrl,
  downloadFormViewFile,
} from './form-view-file-helpers';

function fileNameFromUrl(url) {
  if (!url) {
    return '文件';
  }
  const matched = url.replace(/(.*\/)*([^.?#]+)/i, '$2');
  if (matched && matched !== url) {
    try {
      return decodeURIComponent(matched);
    } catch (e) {
      return matched;
    }
  }
  const part = url.split('/').pop();
  return part ? part.split('?')[0] : '文件';
}

export default {
  name: 'FormViewFileList',
  components: {
    FormViewPreviewDrawer,
  },
  props: {
    files: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      previewVisible: false,
      previewUrl: '',
      previewFileName: '',
    };
  },
  computed: {
    normalizedFiles() {
      if (!Array.isArray(this.files)) {
        return [];
      }
      const cosBase = getCosBase(this);
      return this.files
        .filter((f) => f && (f.url || f.name))
        .map((f) => {
          const url = f.url ? toFullFileUrl(f.url, cosBase) : '';
          const name = f.name || (url ? fileNameFromUrl(url) : '文件');
          const ext = String(f.ext || getFileExt(name) || getFileExt(url) || 'file').toLowerCase();
          return {
            url,
            name,
            ext,
            isImage: isImageExt(ext),
            iconUrl: getFileTypeIconUrl(ext),
          };
        })
        .filter((f) => f.url || f.name);
    },
  },
  methods: {
    openPreview(file) {
      const url = typeof file === 'string' ? file : file && file.url;
      if (!url) {
        return;
      }
      this.previewUrl = toFullFileUrl(url, getCosBase(this));
      this.previewFileName = (file && file.name) || fileNameFromUrl(this.previewUrl);
      this.previewVisible = true;
    },
    download(url, name) {
      downloadFormViewFile(this, url, name);
    },
  },
};
</script>

<style lang="less" scoped>
.ugo-form-view-file-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.ugo-form-view-file-card {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 240px;
  max-width: 100%;
  padding: 10px 12px;
  background: linear-gradient(to bottom right, var(--bg3, #f8f8f9), var(--bg2, #fff));
  border: 1px solid var(--border, #e8eaec);
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  transition: box-shadow 0.2s, border-color 0.2s;

  &:hover {
    border-color: var(--primary, #2d8cf0);
    box-shadow: 0 2px 10px rgba(45, 140, 240, 0.12);
  }
}

.ugo-form-view-file-card-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 6px;
  background: var(--bg2, #fff);
  border: 1px solid var(--border, #e8eaec);
  cursor: pointer;
  overflow: hidden;
}

.ugo-form-view-file-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.ugo-form-view-file-type-icon {
  width: 28px;
  height: 28px;
  object-fit: contain;
}

.ugo-form-view-file-card-body {
  flex: 1;
  min-width: 0;
}

.ugo-form-view-file-card-name {
  display: block;
  color: var(--text3, #515a6e);
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  cursor: pointer;
  word-break: break-all;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    color: var(--primary, #2d8cf0);
  }
}

.ugo-form-view-file-card-actions {
  margin-top: 4px;
  font-size: 12px;
  color: var(--subtext2, #808695);
}

.ugo-form-view-file-action {
  color: var(--primary, #2d8cf0);
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.ugo-form-view-file-sep {
  margin: 0 6px;
  color: var(--border, #dcdee2);
}

.ugo-form-view-empty {
  color: var(--subtext2, #c5c8ce);
}
</style>
